import React, { useState, useEffect } from "react";
import { API_BASE_URL } from './config.js';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container,
  Paper,
  Alert,
  Grid,
  Avatar,
  Divider,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { 
  Person as PersonIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

function MyAccount() {
  const [userData, setUserData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    username: ""
  });
  const [originalData, setOriginalData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  // Fetch user data on component mount
  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    setIsLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("access");
      const username = localStorage.getItem("username");
      
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/user/profile/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = {
        first_name: response.data.first_name || "",
        last_name: response.data.last_name || "",
        email: response.data.email || "",
        username: response.data.username || username || ""
      };
      
      setUserData(data);
      setOriginalData(data);
    } catch (err) {
      console.error("Error fetching user data:", err);
      if (err.response?.status === 401) {
        navigate("/login");
      } else {
        setError(err.response?.data?.error || "Failed to load user data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError("");
    setSuccess("");
  };

  const handleCancel = () => {
    setUserData(originalData);
    setIsEditing(false);
    setError("");
    setSuccess("");
  };

  const handleSave = async () => {
    // Validate inputs
    if (!userData.first_name.trim()) {
      setError("First name is required");
      return;
    }
    if (!userData.last_name.trim()) {
      setError("Last name is required");
      return;
    }
    if (!userData.email.trim()) {
      setError("Email is required");
      return;
    }
    if (!userData.email.includes('@')) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSaving(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("access");
      
      const response = await axios.put(
        `${API_BASE_URL}/api/user/profile/`,
        {
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const updatedData = {
        first_name: response.data.first_name || userData.first_name,
        last_name: response.data.last_name || userData.last_name,
        email: response.data.email || userData.email,
        username: userData.username
      };

      setUserData(updatedData);
      setOriginalData(updatedData);
      setIsEditing(false);
      setSuccess("Profile updated successfully!");
      
      // Update username in localStorage if it changed
      if (response.data.username) {
        localStorage.setItem("username", response.data.username);
      }
    } catch (err) {
      console.error("Error saving user data:", err);
      setError(err.response?.data?.error || "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  // Get initials for avatar
  const getInitials = () => {
    const first = userData.first_name?.charAt(0) || "";
    const last = userData.last_name?.charAt(0) || "";
    return (first + last).toUpperCase() || "U";
  };

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: 'background.default'
      }}>
        <CircularProgress sx={{ color: '#1d5576' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      pb: 4
    }}> 

      <Container maxWidth="md">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ 
            mb: 2, 
            color: '#1d5576',
            '&:hover': {
              backgroundColor: 'rgba(29, 85, 118, 0.04)'
            }
          }}
        >
          Back
        </Button>

        {/* Main Content */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          {/* Header with Avatar */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 4,
            flexDirection: { xs: 'column', sm: 'row' },
            textAlign: { xs: 'center', sm: 'left' }
          }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: '#1d5576',
                fontSize: '2rem',
                mr: { xs: 0, sm: 3 },
                mb: { xs: 2, sm: 0 }
              }}
            >
              {getInitials()}
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ color: '#1d5576', fontWeight: 600, mb: 1 }}>
                {userData.first_name} {userData.last_name}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {userData.username}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          {/* Profile Form */}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={userData.first_name}
                onChange={handleInputChange}
                disabled={!isEditing || isSaving}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: '#1d5576',
                    },
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={userData.last_name}
                onChange={handleInputChange}
                disabled={!isEditing || isSaving}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: '#1d5576',
                    },
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={userData.email}
                onChange={handleInputChange}
                disabled={!isEditing || isSaving}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: '#1d5576',
                    },
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                value={userData.username}
                disabled={true}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#f5f5f5'
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Username cannot be changed
              </Typography>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            mt: 4,
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            {!isEditing ? (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  bgcolor: '#1d5576',
                  '&:hover': {
                    bgcolor: '#17465f',
                  }
                }}
              >
                Edit Profile
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={isSaving}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    borderColor: '#dc3545',
                    color: '#dc3545',
                    '&:hover': {
                      borderColor: '#bd2130',
                      backgroundColor: 'rgba(220, 53, 69, 0.04)'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    bgcolor: '#28a745',
                    '&:hover': {
                      bgcolor: '#218838',
                    },
                    '&:disabled': {
                      bgcolor: '#e0e0e0'
                    }
                  }}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default MyAccount;